import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSendOtp, useVerifyAccessCode, useVerifyOtp } from '@/hooks/api/useAtHomeAuth';
import useApiErrorHandling from '@/hooks/util/useApiErrorHandler';
import useSendEvents from '@/hooks/util/useSendEvents';
import {
  AtHomeLoginResponse,
  GenericApiResponse,
  SendOtpResponse,
  VerifyOtpParameters,
} from '@/services/api';
import { ErrorCode, getErrorMessageFromCode } from '@/utils/errorMessages';

import { HomeFormData } from '../components/HomeSetUpScreen';

interface ValidationParameters {
  accessCode: string;
  programCode: string;
  mobile: string;
}

export type ErrorsState = Partial<ValidationParameters>;

const mapCodeToFieldErrors = (code: ErrorCode, t: (key: string) => string) => {
  const errors: ErrorsState = {};
  switch (code as ErrorCode) {
    case ErrorCode.InvalidProgramCode: {
      errors.programCode = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.InvalidAccessCode: {
      errors.accessCode = getErrorMessageFromCode(code, t);
      break;
    }
    default:
  }
  return errors;
};

const useAtHomeScreen = () => {
  const { t } = useTranslation();
  const { handleApiError } = useApiErrorHandling((code) => mapCodeToFieldErrors(code, t));
  const { mutate: verifyAccessCode, isPending: isVerifyingAccessCode } = useVerifyAccessCode();
  const { mutate: sendOtp } = useSendOtp();
  const { mutate: verifyOtp } = useVerifyOtp();
  const { programLoginStartEvent, programLoginFailureEvent, programLoginSuccessEvent } =
    useSendEvents();

  const onSubmitAtHomeAccessCode = useCallback(
    (
      parameters: HomeFormData,
      onSuccess?: (data: GenericApiResponse<AtHomeLoginResponse>) => void,
    ) => {
      programLoginStartEvent(parameters.programCode, 'HOME_ACCESS_CODE');
      verifyAccessCode(
        { accessCode: parameters.accessCode, programCode: parameters.programCode },
        {
          onSuccess: (data) => {
            if (data.data.code) {
              programLoginFailureEvent(parameters.programCode, data.data.code.toString());
              handleApiError(data.data.code);
            } else if (onSuccess) {
              programLoginSuccessEvent(parameters.programCode, 'HOME_ACCESS_CODE');
              localStorage.setItem('atHomeLoginResponse', JSON.stringify(data.data));
              localStorage.setItem('accessToken', data.data.accessToken.token);
              localStorage.setItem('refreshToken', data.data.refreshToken);
              localStorage.setItem('programCode', parameters.programCode);
              onSuccess(data);
            }
          },
        },
      );
    },
    [
      verifyAccessCode,
      handleApiError,
      programLoginFailureEvent,
      programLoginStartEvent,
      programLoginSuccessEvent,
    ],
  );

  const onSubmitAtHomeMobile = useCallback(
    (parameters: HomeFormData, onSuccess?: (data: GenericApiResponse<SendOtpResponse>) => void) => {
      programLoginStartEvent(parameters.programCode, 'HOME_MOBILE');
      sendOtp(
        { mobile: parameters.mobile, programCode: parameters.programCode },
        {
          onSuccess: (data) => {
            if (data.data.code) {
              programLoginFailureEvent(parameters.programCode, data.data.code.toString());
              handleApiError(data.data.code);
            } else if (onSuccess) {
              programLoginSuccessEvent(parameters.programCode, 'HOME_MOBILE');
              localStorage.setItem('programCode', parameters.programCode);
              onSuccess(data);
            }
          },
        },
      );
    },
    [
      sendOtp,
      handleApiError,
      programLoginFailureEvent,
      programLoginStartEvent,
      programLoginSuccessEvent,
    ],
  );

  const onSubmitOtp = useCallback(
    (
      parameters: VerifyOtpParameters,
      onSuccess?: (data: GenericApiResponse<AtHomeLoginResponse>) => void,
    ) => {
      verifyOtp(
        { mobile: parameters.mobile, otp: parameters.otp },
        {
          onSuccess: (data) => {
            if (data.data.code) {
              handleApiError(data.data.code);
            } else if (onSuccess) {
              localStorage.setItem('atHomeLoginResponse', JSON.stringify(data.data));
              localStorage.setItem('accessToken', data.data.accessToken.token);
              onSuccess(data);
            }
          },
        },
      );
    },
    [verifyOtp, handleApiError],
  );

  return {
    onSubmitAtHomeAccessCode,
    onSubmitAtHomeMobile,
    isVerifyingAccessCode,
    onSubmitOtp,
  };
};

export default useAtHomeScreen;
